import { matchesOption, displayOptionInput } from "../../../i18n/options";
import { useLanguage, t } from "../../../i18n";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Form, Row, Col, Button } from "react-bootstrap";
import QuickFilters from "./QuickFilters";
import "./ListingFilters.css";
const normalizeOption = option => {
  if (typeof option === "object" && option !== null) {
    return {
      value: String(option.value ?? option.id ?? ""),
      label: String(option.label ?? option.name ?? ""),
      raw: option
    };
  }
  return {
    value: String(option),
    label: String(option),
    raw: option
  };
};
function SearchableSingleSelectField({
  label,
  value,
  options,
  placeholder,
  emptyMessage,
  disabled = false,
  loading = false,
  onChange
}) {
  const {
    language
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const wrapperRef = useRef(null);
  const normalizedOptions = useMemo(() => Array.isArray(options) ? options.map(normalizeOption) : [], [options]);
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const filteredOptions = useMemo(() => {
    const keyword = String(inputValue || "").trim().toLowerCase();
    if (!keyword) return normalizedOptions;
    return normalizedOptions.filter(item => matchesOption(item, keyword, language));
  }, [inputValue, normalizedOptions, language]);
  const handleSelect = option => {
    setInputValue(option.label);
    setIsOpen(false);
    onChange(option.label);
  };
  return <Form.Group>
      <Form.Label>{t(label)}</Form.Label>

      <div ref={wrapperRef} className={`bb-search-select ${disabled ? "disabled" : ""}`}>

        <Form.Control type="text" value={displayOptionInput(inputValue, normalizedOptions)} placeholder={t(loading ? "Loading..." : placeholder)} disabled={disabled} autoComplete="off" onFocus={() => {
        if (!disabled) {
          setIsOpen(true);
        }
      }} onChange={e => {
        const nextValue = e.target.value;
        setInputValue(nextValue);
        setIsOpen(true);
        // Search text is local; only an option selection submits a canonical label.
        if (!nextValue) onChange("");
      }} onBlur={() => {
        setInputValue(value || "");
        setIsOpen(false);
      }} />


        {isOpen && !disabled ? <div className="bb-search-select__menu">
            {filteredOptions.length ? filteredOptions.map(option => <button key={option.value} type="button" className={`bb-search-select__option ${value === option.label ? "active" : ""}`} onMouseDown={e => e.preventDefault()} onClick={() => handleSelect(option)}>

                  {t(option.label)}
                </button>) : <div className="bb-search-select__empty">
                {t(emptyMessage || "No relevant option found")}
              </div>}
          </div> : null}
      </div>
    </Form.Group>;
}
function MultiSelectTagField({
  label,
  options,
  selectedValues,
  helperText,
  searchable = false,
  searchPlaceholder,
  disabled = false,
  loading = false,
  onChange
}) {
  const {
    language
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const normalizedOptions = useMemo(() => Array.isArray(options) ? options.map(normalizeOption) : [], [options]);
  const selectedList = Array.isArray(selectedValues) ? selectedValues : [];
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);
  const filteredOptions = useMemo(() => {
    if (!searchable) return normalizedOptions;
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return normalizedOptions;
    return normalizedOptions.filter(item => matchesOption(item, keyword, language));
  }, [normalizedOptions, searchable, searchKeyword, language]);
  const isSelected = value => selectedList.includes(String(value));
  const handleToggleOption = value => {
    const stringValue = String(value);
    if (isSelected(stringValue)) {
      onChange(selectedList.filter(item => item !== stringValue));
      return;
    }
    onChange([...selectedList, stringValue]);
  };
  const handleRemoveTag = (valueToRemove, event) => {
    event.stopPropagation();
    onChange(selectedList.filter(item => item !== String(valueToRemove)));
  };
  const selectedLabelMap = useMemo(() => {
    const map = {};
    normalizedOptions.forEach(item => {
      map[item.value] = item.label;
    });
    return map;
  }, [normalizedOptions]);
  return <Form.Group>
      <Form.Label>{t(label)}</Form.Label>

      <div ref={wrapperRef} className={`bb-tag-multiselect ${disabled ? "disabled" : ""}`}>

        <div className="bb-tag-multiselect__control" onClick={() => {
        if (!disabled) {
          setIsOpen(prev => !prev);
        }
      }} role="button" tabIndex={disabled ? -1 : 0} onKeyDown={e => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsOpen(prev => !prev);
        }
      }}>

          <div className="bb-tag-multiselect__value">
            {loading ? <span className="bb-tag-multiselect__placeholder">{t("Loading...")}</span> : selectedList.length === 0 ? <span className="bb-tag-multiselect__placeholder">{t("Select ")}
              {t(label)}
              </span> : <div className="bb-tag-multiselect__tags">
                {selectedList.map(item => <span key={item} className="bb-tag-multiselect__tag">
                    {t(selectedLabelMap[item] || item)}
                    <button type="button" className="bb-tag-multiselect__tag-remove" onClick={e => handleRemoveTag(item, e)}>

                      ×
                    </button>
                  </span>)}
              </div>}
          </div>

          <div className="bb-tag-multiselect__actions">
            <span className={`bb-tag-multiselect__arrow ${isOpen ? "open" : ""}`}>
              ▾
            </span>
          </div>
        </div>

        {isOpen && !disabled ? <div className="bb-tag-multiselect__menu">
            {searchable ? <div className="bb-tag-multiselect__search-wrap">
                <Form.Control ref={searchInputRef} type="text" value={searchKeyword} placeholder={t(searchPlaceholder || "Search")} onChange={e => setSearchKeyword(e.target.value)} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} />

              </div> : null}

            {filteredOptions.length ? filteredOptions.map(option => <button key={option.value} type="button" className={`bb-tag-multiselect__option ${isSelected(option.value) ? "active" : ""}`} onClick={() => handleToggleOption(option.value)}>

                  <span className="bb-tag-multiselect__checkbox">
                    {t(isSelected(option.value) ? "✓" : "")}
                  </span>
                  <span>{t(option.label)}</span>
                </button>) : <div className="bb-tag-multiselect__empty">{t("No relevant options found")}

          </div>}
          </div> : null}
      </div>

      {helperText ? <div className="bb-listing-form__helper-text">{t(helperText)}</div> : null}
    </Form.Group>;
}
export default function ListingFilters({
  search,
  selectedCity,
  selectedDistrict,
  selectedNationality,
  selectedHouseType,
  selectedRoomType,
  selectedFurnished,
  selectedAvailability = "",
  selectedGender,
  minRent,
  maxRent,
  cityOptions,
  districtOptions,
  nationalityOptions,
  houseTypeOptions,
  roomTypeOptions,
  furnishedOptions,
  genderOptions,
  showAdvanced,
  onToggleAdvanced,
  onSearchChange,
  onCityChange,
  onDistrictChange,
  onNationalityChange,
  onHouseTypeChange,
  onRoomTypeChange,
  onFurnishedChange,
  onAvailabilityChange,
  onGenderChange,
  onMinRentChange,
  onMaxRentChange,
  onReset,
  minRentError,
  maxRentError
}) {
  useLanguage();
  return <Card className="bb-listing-filters-card">
      <Card.Body>
        <Row className="g-3 align-items-end">
          <Col xs={12}>
            <Form.Group>
              <Form.Label>{t("Search Listings")}</Form.Label>
              <Form.Control type="text" placeholder={t("Search by title, neighbourhood, or nationality")} value={search} onChange={e => onSearchChange(e.target.value)} />

            </Form.Group>
          </Col>

        </Row>

        <QuickFilters selectedFurnished={selectedFurnished} selectedRoomType={selectedRoomType} selectedAvailability={selectedAvailability} onFurnishedChange={onFurnishedChange} onRoomTypeChange={onRoomTypeChange} onAvailabilityChange={onAvailabilityChange} showAdvanced={showAdvanced} onToggleAdvanced={onToggleAdvanced} />

        {showAdvanced ? <div id="listing-advanced-filters" className="bb-listing-filters__advanced-wrap">
            <Row className="g-3">
              <Col md={6} lg={3}>
                <SearchableSingleSelectField label={t("City")} value={selectedCity} options={cityOptions} placeholder={t("Search city")} emptyMessage={t("No relevant city found")} onChange={onCityChange} />

              </Col>

              <Col md={6} lg={3}>
                <SearchableSingleSelectField label={t("District / Neighbourhood")} value={selectedDistrict} options={districtOptions} placeholder={t("Search district / neighbourhood")} emptyMessage={t("No relevant district / neighbourhood found")} onChange={onDistrictChange} disabled={!selectedCity} />

              </Col>

              <Col md={6} lg={3}>
                <SearchableSingleSelectField label={t("Nationality")} value={selectedNationality} options={nationalityOptions} placeholder={t("Search nationality")} emptyMessage={t("No relevant nationality found")} onChange={onNationalityChange} />

              </Col>

              <Col md={6} lg={3}>
                <Form.Group>
                  <Form.Label>{t("Gender")}</Form.Label>
                  <Form.Select value={selectedGender} onChange={e => onGenderChange(e.target.value)}>

                    <option value="">{t("Select Gender")}</option>
                    {genderOptions.map(gender => <option key={gender} value={gender}>
                        {t(gender)}
                      </option>)}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <Form.Group>
                  <Form.Label>{t("Min Rent (SAR)")}</Form.Label>
                  <Form.Control type="text" inputMode="numeric" placeholder={t("Minimum 100 SAR")} value={minRent} onChange={e => onMinRentChange(e.target.value)} isInvalid={!!minRentError} />

                  {minRentError ? <Form.Control.Feedback type="invalid">
                      {t(minRentError)}
                    </Form.Control.Feedback> : null}
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <Form.Group>
                  <Form.Label>{t("Max Rent (SAR)")}</Form.Label>
                  <Form.Control type="text" inputMode="numeric" placeholder={t("Maximum 50000 SAR")} value={maxRent} onChange={e => onMaxRentChange(e.target.value)} isInvalid={!!maxRentError} />

                  {maxRentError ? <Form.Control.Feedback type="invalid">
                      {t(maxRentError)}
                    </Form.Control.Feedback> : null}
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <MultiSelectTagField label={t("House Type")} options={houseTypeOptions} selectedValues={selectedHouseType} searchable searchPlaceholder={t("Search house type")} helperText={t("You can select one or multiple house types.")} onChange={onHouseTypeChange} />

              </Col>

              <Col md={6} lg={3}>
                <MultiSelectTagField label={t("Room Type")} options={roomTypeOptions} selectedValues={selectedRoomType} searchable searchPlaceholder={t("Search room type")} helperText={t("You can select one or multiple room types.")} onChange={onRoomTypeChange} />

              </Col>

              <Col md={6} lg={3}>
                <Form.Group controlId="listing-furnished-filter">
                  <Form.Label>{t("Furnished")}</Form.Label>
                  <Form.Select value={selectedFurnished} onChange={e => onFurnishedChange(e.target.value)}>

                    <option value="">{t("Any")}</option>
                    {furnishedOptions.map(item => <option key={item} value={item}>
                        {t(item)}
                      </option>)}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <Form.Group controlId="listing-availability-filter">
                  <Form.Label>{t("Availability")}</Form.Label>
                  <Form.Select value={selectedAvailability} onChange={e => onAvailabilityChange(e.target.value)}>
                    <option value="">{t("Any")}</option>
                    <option value="immediate">{t("Available Now")}</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <Button variant="outline-secondary" className="w-100 bb-listing-filters__reset-btn" onClick={onReset}>{t("Reset")}


              </Button>
              </Col>
            </Row>
          </div> : null}
      </Card.Body>
    </Card>;
}
